import React, { useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import axios from "axios";
import { getToken, getUser } from "../utils/session";
import { validateSelectedFiles } from "../utils/uploadValidation";
import "./ChatWindow.css";

import { API_BASE } from "../utils/apiConfig";

function FileName({ value }) {
  const lastDot = value.lastIndexOf(".");
  const hasExtension = lastDot > 0 && lastDot < value.length - 1;
  const base = hasExtension ? value.slice(0, lastDot) : value;
  const extension = hasExtension ? value.slice(lastDot) : "";

  return (
    <span className="file-name" title={value}>
      <span className="file-name-base">{base}</span>
      {extension && <span className="file-name-extension">{extension}</span>}
    </span>
  );
}

function ChatWindow({ messages, isTyping, chatId, mode, onEnsureChat, onSend, onAdmin }) {
  const chatRef = useRef(null);
  const token = getToken();

  const userName =
    localStorage.getItem("logicline_user_name") || "there";

  const [files, setFiles] = useState([]);
  const [copiedIndex, setCopiedIndex] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [uploadStage, setUploadStage] = useState("uploading");
  const [uploadingFile, setUploadingFile] = useState(null);
  const [uploadError, setUploadError] = useState(null);
  const [isDragging, setIsDragging] = useState(false);

  const isWelcome = messages.length === 0;

  const statusFor = (msg) => {
    if (msg.guardrailStatus === "answered" && msg.sourceType === "regulatory_guidance") {
      return { label: "Evidence + guidance", tone: "supported" };
    }
    if (msg.guardrailStatus === "answered") return { label: "Evidence supported", tone: "supported" };
    if (msg.guardrailStatus?.startsWith("blocked")) return { label: "Safety boundary", tone: "guarded" };
    if (["not_supported", "missing_valid_citations", "no_regulatory_support"].includes(msg.guardrailStatus)) {
      return { label: "Insufficient evidence", tone: "limited" };
    }
    if (msg.sourceType === "not_supported") return { label: "Insufficient evidence", tone: "limited" };
    if (msg.sourceType === "analysis_unavailable") return { label: "Analysis unavailable", tone: "limited" };
    if (msg.sourceType === "inspection") return { label: "Inspection simulation", tone: "inspection" };
    return null;
  };

  /* =========================
     LOAD FILES FOR CHAT
  ========================= */
  useEffect(() => {
    if (!chatId || !token) {
      setFiles([]);
      return;
    }

    axios
      .get(`${API_BASE}/chats/${chatId}/files`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => setFiles(res.data || []))
      .catch(() => setFiles([]));
  }, [chatId, token]);

  /* =========================
     SCROLL ON NEW MESSAGES
  ========================= */
  useEffect(() => {
    chatRef.current?.scrollTo({
      top: chatRef.current.scrollHeight,
      behavior: "smooth",
    });
  }, [messages, isTyping]);

  const uploadFiles = async (selectedFiles) => {
    const validationError = validateSelectedFiles(selectedFiles);
    if (validationError) {
      setUploadError(validationError);
      setUploadingFile(null);
      setUploadProgress(null);
      return;
    }

    let activeId = chatId;
    if (!activeId && onEnsureChat) {
      activeId = await onEnsureChat();
    }

    if (!activeId || !token) {
      console.warn("DEBUG: Missing chatId or token after ensure. Aborting.");
      return;
    }

    setUploadError(null);
    setUploadingFile(selectedFiles.length === 1 ? selectedFiles[0].name : `${selectedFiles.length} files`);
    setUploadProgress(0);
    setUploadStage("uploading");

    const formData = new FormData();
    selectedFiles.forEach((file) => formData.append("files", file));

    try {
      await axios.post(`${API_BASE}/chats/${activeId}/upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`
        },
        onUploadProgress: (e) => {
          if (!e.total) return;
          const prog = Math.round((e.loaded / e.total) * 100);
          if (prog >= 100) {
            setUploadStage("processing");
            setUploadProgress(95);
          } else {
            setUploadProgress(Math.min(prog, 95));
          }
        },
      });

      setUploadProgress(100);
      setUploadStage("complete");

      const refreshedFiles = await axios.get(
        `${API_BASE}/chats/${activeId}/files`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setFiles(refreshedFiles.data || []);
    } catch (err) {
      const message =
        err?.response?.data?.error ||
        (err.code === "ECONNABORTED" ? "Upload timed out. Please try a smaller file." : null) ||
        "Upload failed. Please check the file type and try again.";
      setUploadError(message);
      console.error("DEBUG: Upload failed details:", {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status,
        headers: err.response?.headers
      });
    } finally {
      setUploadingFile(null);
      setUploadProgress(null);
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files?.length) {
      uploadFiles(Array.from(e.target.files));
      e.target.value = "";
    }
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setIsDragging(false);
    const droppedFiles = Array.from(event.dataTransfer.files || []);
    if (droppedFiles.length) uploadFiles(droppedFiles);
  };

  const removeFile = async (filename) => {
    try {
      await axios.delete(`${API_BASE}/chats/${chatId}/files/${filename}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setFiles((prev) => prev.filter((f) => f.filename !== filename));
    } catch (err) {
      setUploadError(err?.response?.data?.error || "The document could not be removed. Please try again.");
    }
  };

  const handleCopy = async (text, index) => {
    await navigator.clipboard.writeText(text);
    setCopiedIndex(index);
    setTimeout(() => setCopiedIndex(null), 1200);
  };

  return (
    <div className="chat-layout">
      {/* ================= CHAT ================= */}
      <div className="chat-window" ref={chatRef}>
        {mode === "inspection" && (
          <div className="mode-badge-wrapper">
             <span className="mode-badge audit">🕵️ LIVE AUDIT MODE</span>
          </div>
        )}
        {isWelcome ? (
          <div className="welcome-wrapper">
            <div className="welcome-card">
              <span className="welcome-eyebrow">Controlled document intelligence</span>
              <h1>Welcome, {userName}</h1>
              <p className="welcome-subtitle">
                Upload relevant evidence, then choose a focused review. LogicLine will show when an answer is supported, limited, or requires human approval.
              </p>
              <div className="welcome-capabilities">
                <button disabled={!files.length || isTyping} onClick={() => onSend("Identify gaps in the uploaded process or procedure")}>Identify process gaps</button>
                <button disabled={!files.length || isTyping} onClick={() => onSend("Create a draft SOP from the uploaded documents")}>Create a draft SOP</button>
                <button disabled={!files.length || isTyping} onClick={() => onSend("Analyze compliance of the uploaded documents")}>Review compliance</button>
              </div>
              {!files.length && <p className="welcome-upload-hint">Upload a document to enable these reviews.</p>}
              <p className="welcome-note">Outputs support qualified review and do not constitute medical, legal, or regulatory sign-off.</p>
            </div>
          </div>
        ) : (
          <>
            {messages.map((msg, i) => (
              <div
                key={i}
                className={`chat-bubble ${msg.sender === "user" ? "user" : "ai"}`}
              >
                {msg.sender === "ai" && statusFor(msg) && (
                  <div className={`answer-status ${statusFor(msg).tone}`}>
                    {statusFor(msg).label}
                  </div>
                )}
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {msg.text}
                </ReactMarkdown>

                {msg.sender === "ai" && msg.citations?.length > 0 && (
                  <details className="answer-sources">
                    <summary>Sources used ({msg.citations.length})</summary>
                    <ul>
                      {msg.citations.map((citation) => (
                        <li key={citation.id}>
                          <span>{citation.label}</span>
                          <small>{citation.type === "regulatory_guidance" ? "Regulatory guidance" : "Uploaded document"}</small>
                        </li>
                      ))}
                    </ul>
                  </details>
                )}

                {msg.sender === "ai" && (
                  <div className="message-actions">
                    <button
                      className="copy-btn"
                      onClick={() => handleCopy(msg.text, i)}
                    >
                      {copiedIndex === i ? "Copied" : "Copy"}
                    </button>
                  </div>
                )}
              </div>
            ))}

            {isTyping && (
              <div className="chat-bubble ai typing">
                <span className="dot" />
                <span className="dot" />
                <span className="dot" />
              </div>
            )}
          </>
        )}
      </div>

      {/* ================= FILE PANEL ================= */}
      <aside className="file-panel">
        <div className="file-panel-header">
          <h3>Documents</h3>
          {["admin", "client_admin"].includes(getUser()?.role) && (
            <button className="small-admin-btn" onClick={onAdmin}>
              ⚙️ Admin Panel
            </button>
          )}
        </div>

        <div
          className={`upload-dropzone ${isDragging ? "dragging" : ""}`}
          onDragEnter={(event) => { event.preventDefault(); setIsDragging(true); }}
          onDragOver={(event) => event.preventDefault()}
          onDragLeave={() => setIsDragging(false)}
          onDrop={handleDrop}
        >
          <div className="drop-icon">⬆</div>
          <p>Drag files to upload</p>
          <span>or</span>
          <label className="browse-btn">
            Browse files
            <input
              type="file"
              multiple
              accept=".pdf,.docx,.xlsx,.xls,.txt,.csv,.jpg,.jpeg,.png,.webp"
              onChange={handleFileChange}
            />
          </label>
          <small>PDF, DOCX, XLSX, XLS, TXT, CSV, JPG, PNG, WEBP. Max 15MB each.</small>
        </div>

        <div className="file-list">
          {uploadError && (
            <div className="file-row failed">
              <span className="file-name">{uploadError}</span>
            </div>
          )}

          {uploadingFile && (
            <div className="file-row uploading">
              <div className="file-row-top">
                <FileName value={uploadingFile} />
                <span className="file-percentage">
                  {uploadStage === "processing" ? "Processing…" : `${uploadProgress}%`}
                </span>
              </div>

              <div className="file-progress">
                <div style={{ width: `${uploadProgress}%` }}></div>
              </div>
            </div>
          )}

          {files.map((file) => (
            <div key={file.filename} className="file-row completed">
              <div className="file-row-top">
                <FileName value={file.filename} />
                <button
                  className="remove-file-btn"
                  onClick={() => removeFile(file.filename)}
                >
                  ✕
                </button>
              </div>
            </div>
          ))}
        </div>

        {files.length > 0 && (
          <div className="quick-actions">
            <h4>Quick Actions</h4>
            <button 
              className="action-btn compliance"
              onClick={() => onSend("Analyze compliance of the uploaded documents")}
              disabled={isTyping}
            >
              🔍 Run Compliance Review
            </button>
            <button
              className="action-btn gaps"
              onClick={() => onSend("Identify gaps in the uploaded process or procedure")}
              disabled={isTyping}
            >
              Identify Process Gaps
            </button>
            <button
              className="action-btn sop"
              onClick={() => onSend("Create a draft SOP from the uploaded documents")}
              disabled={isTyping}
            >
              Create Draft SOP
            </button>
            <button 
              className="action-btn inspection"
              onClick={() => onSend("Start inspection")}
              disabled={isTyping}
            >
              🕵️ Start Interactive Audit
            </button>
          </div>
        )}
      </aside>
    </div>
  );
}

export default ChatWindow;
